import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    Clock,
    CheckCircle2,
    XCircle,
    RefreshCw,
    HeartHandshake,
    ChevronDown,
    Camera,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import Logo from "../components/Logo";
import PhotoCapture from "../components/PhotoCapture";

/**
 * Waiting room shown to users with role=candidate.
 * Iter-34 — handles 3 states:
 *  1. Google self-signup missing photo/association → completion form
 *  2. Pending approval → waiting steps + "check status" button
 *  3. Rejected → explanation
 */
export default function CandidateWaiting() {
    const { user, logout, setUser } = useAuth();
    const [checkingStatus, setCheckingStatus] = useState(false);

    const isCoopt = user?.signup_kind === "cooptation";
    const rejected = user?.candidate_status === "rejected";
    const needsCompletion =
        !rejected && !isCoopt && (!user?.has_photo || !user?.desired_association_id);

    const checkStatus = async (silent = false) => {
        setCheckingStatus(true);
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            if (!silent && data.role === "candidate") {
                toast.info("Ton inscription est toujours en cours de validation.");
            }
        } catch (e) { /* ignore */ } finally {
            setCheckingStatus(false);
        }
    };

    // Hydrate depuis /auth/me au mount : les réponses login/register/Google callback
    // peuvent être plus pauvres que /auth/me (has_photo, asso souhaitée, statut).
    useEffect(() => {
        checkStatus(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className="min-h-screen flex items-center justify-center bg-shariti-deep-black px-6 py-10 relative overflow-hidden"
            data-testid="candidate-waiting-screen"
        >
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-shariti-orange/15 blur-[120px] animate-blob" />
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-lg relative z-10 text-center"
            >
                <div className="mb-6 flex justify-center">
                    <Logo
                        variant="full"
                        showAcademy
                        academySize="md"
                        className="h-20 w-auto drop-shadow-[0_0_24px_rgba(238,114,46,0.35)]"
                    />
                </div>

                {rejected ? (
                    <RejectedCard user={user} logout={logout} />
                ) : needsCompletion ? (
                    <CompleteProfileCard user={user} onDone={() => checkStatus(true)} />
                ) : (
                    <WaitingCard
                        user={user}
                        isCoopt={isCoopt}
                        logout={logout}
                        checkStatus={checkStatus}
                        checkingStatus={checkingStatus}
                    />
                )}
            </motion.div>
        </div>
    );
}

function WaitingCard({ user, isCoopt, logout, checkStatus, checkingStatus }) {
    return (
        <div className="glass-card rounded-3xl p-8" data-testid="candidate-waiting-card">
            <div className="w-16 h-16 rounded-2xl bg-shariti-orange/15 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-shariti-orange" />
            </div>
            <p className="text-xs font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-2">
                {isCoopt ? "Candidature en cours" : "Inscription en cours de validation"}
            </p>
            <h1 className="text-3xl font-extrabold text-white mb-3">
                Bienvenue {user?.name} !
            </h1>
            <p className="text-white/70 leading-relaxed mb-5">
                {isCoopt
                    ? "Ta candidature a bien été enregistrée. Un manager Shariti va l'examiner sous peu et te validera comme recruteur."
                    : "Ton inscription a bien été envoyée. L'équipe Shariti la valide sous peu — reviens vérifier d'ici quelques heures."}
            </p>

            {!isCoopt && user?.desired_association_name && (
                <div
                    className="flex items-center justify-center gap-2 mb-5 text-sm text-white/70"
                    data-testid="waiting-association-name"
                >
                    <HeartHandshake className="w-4 h-4 text-shariti-orange-light" />
                    Association : {" "}
                    <span className="font-bold text-white">{user.desired_association_name}</span>
                </div>
            )}

            <div className="grid grid-cols-1 gap-2 mb-6 text-left">
                <Step done text={isCoopt ? "Inscription via lien de cooptation" : "Inscription envoyée"} />
                <Step text={isCoopt ? "Validation par un manager (en cours...)" : "Validation par l'équipe Shariti (en cours...)"} pending />
                <Step text="Accès à la formation Shariti Academy" />
            </div>

            {isCoopt && user?.referrer_name && (
                <p className="text-xs text-white/40 italic mb-4">
                    Tu as été coopté·e par {user.referrer_name}.
                </p>
            )}

            <button
                onClick={() => checkStatus(false)}
                disabled={checkingStatus}
                data-testid="candidate-check-status"
                className="w-full flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60 mb-4"
            >
                <RefreshCw className={`w-4 h-4 ${checkingStatus ? "animate-spin" : ""}`} />
                Vérifier si mon accès est validé
            </button>

            <button
                onClick={logout}
                data-testid="candidate-logout"
                className="text-sm text-white/50 hover:text-white transition-all underline underline-offset-4"
            >
                Se déconnecter
            </button>
        </div>
    );
}

function CompleteProfileCard({ user, onDone }) {
    const [photo, setPhoto] = useState(null);
    const [associationId, setAssociationId] = useState(user?.desired_association_id || "");
    const [associations, setAssociations] = useState([]);
    const [saving, setSaving] = useState(false);
    const { logout } = useAuth();

    useEffect(() => {
        api.get("/public/associations")
            .then(({ data }) => setAssociations(data))
            .catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!user?.has_photo && !photo) {
            toast.error("Ajoute une photo de toi pour valider ton inscription.");
            return;
        }
        if (!associationId) {
            toast.error("Choisis l'association pour laquelle tu as postulé.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/me/candidate-profile", {
                photo: photo || undefined,
                association_id: associationId,
            });
            toast.success("Inscription complétée ! Elle sera validée sous peu.");
            onDone();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="glass-card rounded-3xl p-8 text-left" data-testid="candidate-complete-profile">
            <div className="w-16 h-16 rounded-2xl bg-shariti-orange/15 flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-shariti-orange" />
            </div>
            <p className="text-xs font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-2 text-center">
                Une dernière étape
            </p>
            <h1 className="text-2xl font-extrabold text-white mb-2 text-center">
                Complète ton inscription
            </h1>
            <p className="text-white/60 text-sm leading-relaxed mb-6 text-center">
                Pour que l'équipe Shariti valide ton accès, indique ton association et
                ajoute une photo de toi.
            </p>

            <form onSubmit={submit} className="space-y-4">
                <div className="relative">
                    <HeartHandshake className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
                    <select
                        value={associationId}
                        onChange={(e) => setAssociationId(e.target.value)}
                        required
                        data-testid="complete-association-select"
                        className={`w-full pl-12 pr-10 py-3.5 glass-card rounded-2xl bg-transparent appearance-none focus:outline-none focus:border-shariti-orange/60 ${
                            associationId ? "text-white" : "text-white/30"
                        }`}
                    >
                        <option value="" className="bg-shariti-deep-black text-white/60">
                            L'association pour laquelle tu as postulé…
                        </option>
                        {associations.map((a) => (
                            <option
                                key={a.association_id}
                                value={a.association_id}
                                className="bg-shariti-deep-black text-white"
                            >
                                {a.name}
                            </option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                </div>

                {!user?.has_photo && <PhotoCapture value={photo} onChange={setPhoto} />}

                <button
                    type="submit"
                    disabled={saving}
                    data-testid="complete-profile-submit"
                    className="w-full flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                >
                    {saving ? "Envoi..." : "Envoyer mon inscription"}
                </button>
            </form>

            <div className="text-center mt-5">
                <button
                    onClick={logout}
                    data-testid="candidate-logout"
                    className="text-sm text-white/50 hover:text-white transition-all underline underline-offset-4"
                >
                    Se déconnecter
                </button>
            </div>
        </div>
    );
}

function RejectedCard({ user, logout }) {
    return (
        <div className="glass-card rounded-3xl p-8" data-testid="candidate-rejected-card">
            <div className="w-16 h-16 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-extrabold text-white mb-3">
                Inscription non retenue
            </h1>
            <p className="text-white/70 leading-relaxed mb-4">
                Ton inscription n'a pas été validée par l'équipe Shariti.
            </p>
            {user?.rejection_reason && (
                <p className="text-sm text-red-300/90 italic mb-5" data-testid="rejection-reason">
                    « {user.rejection_reason} »
                </p>
            )}
            <p className="text-sm text-white/50 mb-6">
                Si tu penses qu'il s'agit d'une erreur, contacte ton responsable Shariti.
            </p>
            <button
                onClick={logout}
                data-testid="candidate-logout"
                className="text-sm text-white/50 hover:text-white transition-all underline underline-offset-4"
            >
                Se déconnecter
            </button>
        </div>
    );
}

function Step({ text, done, pending }) {
    return (
        <div
            className={`flex items-center gap-3 px-4 py-2.5 rounded-2xl ${
                done
                    ? "bg-emerald-500/10 border border-emerald-500/20"
                    : pending
                      ? "bg-shariti-yellow/10 border border-shariti-yellow/20"
                      : "bg-white/5 border border-white/10"
            }`}
        >
            {done ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : pending ? (
                <Clock className="w-4 h-4 text-shariti-yellow shrink-0 animate-pulse" />
            ) : (
                <div className="w-4 h-4 rounded-full border-2 border-white/30 shrink-0" />
            )}
            <span
                className={`text-sm ${
                    done ? "text-white" : pending ? "text-white" : "text-white/50"
                }`}
            >
                {text}
            </span>
        </div>
    );
}
